<template>
  <div class="step-upload">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div class="upload-zone mt-2"
            :class="{ 'upload-zone--dragging': isDragging, 'upload-zone--has-file': hasFile }"
            @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop"
            @click="triggerFileInput">
            <input ref="fileInput" type="file" accept=".xlsx" style="display: none" @change="onFileSelected" />

            <template v-if="!hasFile">
              <v-icon size="64" color="grey lighten-1" class="mb-4">
                mdi-file-excel-outline
              </v-icon>
              <div class="text-h6 grey--text text--darken-2 mb-2">
                Drop your spreadsheet here
              </div>
              <div class="text-body-2 grey--text mb-4">
                or click to browse your files
              </div>
              <v-btn color="primary" outlined id="wizard-upload-btn">
                <v-icon left>mdi-upload</v-icon>
                Choose File
              </v-btn>
              <div class="text-caption grey--text mt-4">
                Accepts .xlsx files only
              </div>
            </template>

            <template v-else>
              <v-icon size="48" color="success" class="mb-3">
                mdi-file-check
              </v-icon>
              <div class="text-h6 mb-1">{{ fileName }}</div>
              <div class="text-body-2 grey--text mb-4">
                {{ fileSize }}
              </div>
              <v-btn text color="error" @click.stop="removeFile">
                <v-icon left small>mdi-close</v-icon>
                Remove
              </v-btn>
            </template>
          </div>

          <div v-if="isParsing" class="text-center mt-6">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <div class="text-body-1">Reading your file...</div>
          </div>

          <v-alert v-if="error" type="error" class="mt-6" outlined dismissible @input="error = null">
            {{ error }}
          </v-alert>

          <v-alert v-if="templateError" type="error" class="mt-6" outlined>
            <div class="d-flex align-start">
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  File doesn't match the import template
                </div>
                <div class="text-body-2 mb-3">
                  Your file is missing the following required columns:
                </div>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="header in templateError.missingRequired" :key="header" small outlined color="error"
                    class="mr-2 mb-2">
                    {{ header }}
                  </v-chip>
                </div>
                <div class="text-body-2 mt-3">
                  Please download the template and ensure your file has all required columns.
                </div>
              </div>
            </div>
          </v-alert>

          <div class="text-center mt-8">
            <div class="d-flex justify-center align-center mb-2">
              <span class="text-body-2 grey--text">Need the right format?</span>
              <v-divider vertical class="mx-3" style="height: auto;"></v-divider>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="showFieldInfo = true"
                    id="wizard-field-info-btn">
                    <v-icon size="18px" color="primary">mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>View Required Fields</span>
              </v-tooltip>
            </div>
            <v-btn text color="primary" @click="$emit('download-template')" id="wizard-download-template">
              <v-icon left small>mdi-download</v-icon>
              Download Template
            </v-btn>
          </div>

          <v-dialog v-model="showFieldInfo" max-width="700" scrollable>
            <v-card id="wizard-required-fields">
              <v-card-title class="text-subtitle-1 px-4">
                <v-icon left color="primary">mdi-table-column</v-icon>
                Required Fields
                <v-spacer></v-spacer>
                <v-btn icon small @click="showFieldInfo = false">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-4">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 140px; white-space: nowrap;">Column Name</th>
                        <th class="text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="field in requiredFields" :key="field.name">
                        <td style="white-space: nowrap;">
                          <span class="font-weight-medium">{{ field.name }}</span>
                        </td>
                        <td class="text-body-2 grey--text text--darken-2">{{ field.note }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="showFieldInfo = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepUpload',
  props: {
    value: {
      type: Object,
      default: null
    },
    templateError: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isDragging: false,
      isParsing: false,
      error: null,
      selectedFile: null,
      showFieldInfo: false,
      requiredFields: [
        {
          name: 'SectionName',
          note: 'Use the name of the Section where this asset will be located.'
        },
        {
          name: 'CategoryName',
          note: 'Use the name of the Category linked to this asset. This Category must be within the provided SectionName.'
        },
        {
          name: 'Identifier',
          note: 'Enter a unique identifier for this asset - this will be visible on the asset search page and on the individual asset detail page. If you would like to later upload resources in bulk related to this asset the identifier values must be unique.'
        },
        {
          name: 'AssetDetailId',
          note: 'If you are updating an existing asset the asset detail id can be used to link to the existing asset - this can be found in the data export. If you leave this field blank the system will search for an asset with a matching identifier, if none are found then a brand new asset will be created.'
        },
        {
          name: 'Description',
          note: 'This will be visible on the individual asset detail page.'
        },
        {
          name: 'Name',
          note: 'Enter the name of the asset - this will be visible on the asset search page and on the individual asset detail page. If you do not populate this field then the name of the asset will be defaulted to the given identifier.'
        }
      ]
    };
  },
  computed: {
    hasFile() {
      return this.selectedFile !== null;
    },
    fileName() {
      return this.selectedFile ? this.selectedFile.name : '';
    },
    fileSize() {
      if (!this.selectedFile) return '';
      const bytes = this.selectedFile.size;
      if (bytes < 1024) return bytes + ' bytes';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
  },
  methods: {
    triggerFileInput() {
      if (!this.hasFile) {
        this.$refs.fileInput.click();
      }
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },
    onFileSelected(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
      this.$refs.fileInput.value = '';
    },
    handleFile(file) {
      if (!file.name.toLowerCase().endsWith('.xlsx')) {
        this.error = 'Please select an Excel file (.xlsx)';
        return;
      }

      this.error = null;
      this.selectedFile = file;
      this.$emit('file-selected', file);
    },
    removeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
      this.$emit('file-removed');
    }
  }
};
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #bdbdbd;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-zone:hover {
  border-color: var(--v-primary-base, #1976d2);
  background: #f5f9ff;
}

.upload-zone--dragging {
  border-color: var(--v-primary-base, #1976d2);
  background: #e3f2fd;
  border-style: solid;
}

.upload-zone--has-file {
  border-color: var(--v-success-base, #4caf50);
  border-style: solid;
  background: #f1f8f4;
  cursor: default;
}
</style>
